/**
 * SectionCard Component
 * Collapsible detail section with chevron indicator
 * Default collapsed to reduce initial cognitive load (ADHD-inclusive)
 */

import { useState, useCallback, useId } from 'react';
import type { ReactNode } from 'react';
import './SectionCard.css';

interface SectionCardProps {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
  badge?: ReactNode;
}

export function SectionCard({
  title,
  defaultOpen = false,
  children,
  badge,
}: SectionCardProps): ReactNode {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const contentId = useId();

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  return (
    <section className="section-card">
      <button
        className="section-card__header"
        onClick={toggle}
        aria-expanded={isOpen}
        aria-controls={contentId}
        type="button"
      >
        <span className="section-card__title">{title}</span>
        {badge && <span className="section-card__badge">{badge}</span>}
        <span
          className={`section-card__chevron ${isOpen ? 'section-card__chevron--open' : ''}`}
          aria-hidden="true"
        >
          &#8250;
        </span>
      </button>
      <div
        id={contentId}
        className={`section-card__content ${isOpen ? 'section-card__content--open' : ''}`}
        role="region"
        aria-labelledby={undefined}
        hidden={!isOpen}
      >
        <div className="section-card__body">{children}</div>
      </div>
    </section>
  );
}
